/**
 * Bundle Perks Cascade
 *
 * When a bundle line gets removed from the cart, the Type B
 * (threshold-unlocked) perks that were added with it are now orphaned:
 * separate top-level lines pointing at warranties / accessories that
 * the customer never wanted on their own. This module finds those
 * orphans by matching `_bundle_group_id` and removes them in a single
 * /cart/update.js call.
 *
 * Trigger: the existing `update:cart-line-item` event the theme's
 * `<cart-items>` / `<cart-drawer-items>` already emit after every
 * /cart/change.js round-trip. We diff the previous /cart.js snapshot
 * against the new one; if a removed line had `_bundle_group_id` AND
 * was NOT itself a perk (`_added_from !== "threshold_perks"`), we
 * cascade-remove every remaining line carrying the same group id.
 *
 * Cascade direction is strictly one-way: bundle removed -> perks
 * removed. Removing a perk does not touch the bundle.
 */
(function () {
  if (window.__bundlePerksCascadeLoaded) return;
  window.__bundlePerksCascadeLoaded = true;

  var CART_URL = "/cart.js";
  var UPDATE_URL =
    (typeof window !== "undefined" &&
      window.routes &&
      window.routes.cart_update_url) ||
    "/cart/update.js";

  function fetchJson(url, options) {
    return fetch(url, options).then(function (res) {
      if (!res.ok) throw new Error("HTTP " + res.status + " " + url);
      return res.json();
    });
  }

  function getProp(item, key) {
    if (!item || !item.properties) return undefined;
    return item.properties[key];
  }

  function dispatchCartLineItemEvent() {
    try {
      document.dispatchEvent(
        new CustomEvent("update:cart-line-item", {
          bubbles: true,
          cancelable: true,
        })
      );
    } catch (e) {
      /* ignore — old browsers */
    }
  }

  /**
   * Re-render the visible cart UIs (cart page and/or cart drawer)
   * by fetching just the sections they care about. We do NOT call
   * `cart-drawer.renderContents()` directly because that method
   * opens the drawer as a side effect — and we may be triggered on
   * the cart page where the drawer should stay closed.
   */
  function refreshCartSections() {
    var cartItemsEls = document.querySelectorAll(
      "cart-items, cart-drawer-items"
    );
    if (!cartItemsEls.length) {
      // Nothing to render on this page; just notify other listeners.
      dispatchCartLineItemEvent();
      return Promise.resolve();
    }

    // Each CartItems-like element exposes the section list it needs.
    // We merge the unique (id, selector, section) targets across all
    // elements so cart-page + cart-drawer can both refresh in one pass.
    var targets = [];
    var seen = {};
    cartItemsEls.forEach(function (el) {
      if (typeof el.getSectionsToRender !== "function") return;
      var list;
      try {
        list = el.getSectionsToRender();
      } catch (e) {
        return;
      }
      (list || []).forEach(function (s) {
        var key = s.id + "|" + (s.selector || "") + "|" + (s.section || "");
        if (seen[key]) return;
        seen[key] = true;
        targets.push({ section: s, owner: el });
      });
    });

    if (!targets.length) {
      dispatchCartLineItemEvent();
      return Promise.resolve();
    }

    var sectionNames = [];
    var nameSeen = {};
    targets.forEach(function (t) {
      var n = t.section.section;
      if (n && !nameSeen[n]) {
        nameSeen[n] = true;
        sectionNames.push(n);
      }
    });

    var url =
      window.location.pathname + "?sections=" + sectionNames.join(",");

    return fetchJson(url)
      .then(function (parsedState) {
        targets.forEach(function (t) {
          var s = t.section;
          var root = document.getElementById(s.id);
          if (!root) return;
          var inner = s.selector ? root.querySelector(s.selector) : root;
          if (!inner) return;
          var html = parsedState[s.section];
          if (!html) return;
          try {
            var renderer = t.owner;
            var innerHtml =
              typeof renderer.getSectionInnerHTML === "function"
                ? renderer.getSectionInnerHTML(html, s.selector)
                : extractInnerHTML(html, s.selector);
            if (innerHtml != null) inner.innerHTML = innerHtml;
          } catch (e) {
            console.warn("[bundle-perks-cascade] section render failed", e);
          }
        });

        // Let progress bars, savings counters, etc. update too.
        // Re-entry into our own handler is suppressed via the
        // `cleanupInFlight` flag in CartCascade below.
        dispatchCartLineItemEvent();
      })
      .catch(function (e) {
        console.warn(
          "[bundle-perks-cascade] failed to refresh cart sections",
          e
        );
      });
  }

  /**
   * Sync the header cart-count bubbles to the authoritative cart returned
   * by /cart/update.js. The bubbles (#cart-icon-bubble mobile +
   * #cart-icon-bubble-desktop) are rendered inline by the
   * `cart-icon-bubble` snippet — NOT as a .shopify-section — so the
   * section-injection path in refreshCartSections() can't reach them, and
   * the cart-page CartItems lists the bubble without a `section`/`selector`
   * so it's skipped there too. The count <div> only exists when
   * item_count > 0, so we create it when crossing 0 -> N and remove it when
   * the cascade empties the cart (the case where it used to stick at "2").
   */
  function updateCartCountBubbles(cart) {
    if (!cart || typeof cart.item_count !== "number") return;
    var count = cart.item_count;
    ["cart-icon-bubble", "cart-icon-bubble-desktop"].forEach(function (id) {
      var container = document.getElementById(id);
      if (!container) return;
      var countEl = container.querySelector('div[class*="rounded-full"]');
      if (count > 0) {
        if (!countEl) {
          countEl = document.createElement("div");
          countEl.className =
            "absolute flex items-center justify-center w-5 h-5 text-xs text-white rounded-full bg-light-actions-primary -top-2 -right-2";
          container.appendChild(countEl);
        }
        countEl.textContent = String(count);
      } else if (countEl) {
        countEl.remove();
      }
    });
  }

  function extractInnerHTML(html, selector) {
    if (!html) return "";
    try {
      var parsed = new DOMParser().parseFromString(html, "text/html");
      var node = parsed.querySelector(selector || ".shopify-section");
      return node ? node.innerHTML : "";
    } catch (e) {
      return "";
    }
  }

  /**
   * Singleton orchestrator. Keeps a snapshot of the cart at the last
   * known-good state and reacts to update events by diffing.
   */
  function CartCascade() {
    this.previousCart = null;
    this.cleanupInFlight = false;
    this.bootstrapPromise = null;
  }

  CartCascade.prototype.start = function () {
    var self = this;
    this.bootstrapPromise = this.fetchCart().then(function (cart) {
      self.previousCart = cart;
    });

    document.addEventListener("update:cart-line-item", function () {
      self.handleCartChange();
    });
  };

  CartCascade.prototype.fetchCart = function () {
    return fetchJson(CART_URL).catch(function (e) {
      console.warn("[bundle-perks-cascade] /cart.js fetch failed", e);
      return null;
    });
  };

  // Bounded so a runaway loop (e.g. a backend bug that keeps making
  // bundles look "removed" in our diff) can never spin the cart.
  // Two iterations covers the realistic worst case: a second bundle
  // removed by the user during the first cascade's network round-trip.
  var MAX_CASCADE_ITERATIONS = 3;

  CartCascade.prototype.handleCartChange = function () {
    var self = this;

    // Skip events caused by our own cleanup pass, otherwise we'd
    // re-diff the same removal and loop. The flag is set BEFORE we
    // refresh the UI and cleared after the resulting events drain.
    if (this.cleanupInFlight) return;

    // Make sure the initial snapshot has finished loading. If the
    // first user action lands during bootstrap, `previousCart` will
    // be null and we just rebaseline without cascading.
    var ready = this.bootstrapPromise || Promise.resolve();
    ready.then(function () {
      return self.runCascade();
    });
  };

  /**
   * Diff-cascade-refresh loop. Keeps the original `previousCart` as
   * the fixed baseline across iterations so a bundle that the user
   * removes WHILE we're cleaning up the previous removal still gets
   * its perks cascaded. Each iteration:
   *   1. fetch fresh /cart.js
   *   2. compute removed-bundle group ids vs baseline
   *   3. find orphan perk lines in the fresh cart for those ids
   *   4. /cart/update.js to remove them; refresh visible cart UIs
   * Stops as soon as a pass finds no orphans.
   */
  CartCascade.prototype.runCascade = function () {
    var self = this;
    var baseline = this.previousCart;
    var iterationsLeft = MAX_CASCADE_ITERATIONS;

    function step() {
      if (iterationsLeft-- <= 0) {
        return self.fetchCart().then(function (finalCart) {
          self.previousCart = finalCart || baseline;
        });
      }

      return self.fetchCart().then(function (newCart) {
        if (!newCart) return;

        if (!baseline) {
          self.previousCart = newCart;
          return;
        }

        var newKeys = {};
        (newCart.items || []).forEach(function (item) {
          if (item && item.key) newKeys[item.key] = true;
        });

        var removedBundleGroupIds = {};
        (baseline.items || []).forEach(function (prevItem) {
          if (!prevItem || !prevItem.key) return;
          // Quantity-only changes keep the same `key`, so they stay
          // in `newKeys` and never qualify as "removed". Cascade is
          // strictly gated on full removal.
          if (newKeys[prevItem.key]) return;
          var groupId = getProp(prevItem, "_bundle_group_id");
          if (!groupId) return;
          // Only the bundle PARENT triggers a cascade when removed.
          // Perks and add-ons share the same _bundle_group_id (for
          // cart sorting) but removing either must NOT cascade to the
          // perks — only removing the bundle itself should.
          if (getProp(prevItem, "_added_from") === "threshold_perks") return;
          if (getProp(prevItem, "_added_from_location") === "add-ons") return;
          removedBundleGroupIds[groupId] = true;
        });

        if (Object.keys(removedBundleGroupIds).length === 0) {
          // No new bundle removals to react to. Rebaseline so the
          // next legitimate event diffs against the post-cascade
          // cart, not the original baseline that still references
          // bundles we already cleaned up after.
          self.previousCart = newCart;
          return;
        }

        var orphans = (newCart.items || []).filter(function (item) {
          var gid = getProp(item, "_bundle_group_id");
          if (!gid || !removedBundleGroupIds[gid]) return false;
          // Only ever remove the perk children. If anything else is
          // somehow tagged with the same group id we leave it alone.
          return getProp(item, "_added_from") === "threshold_perks";
        });

        if (orphans.length === 0) {
          // Bundle(s) gone but no perk lines left to clean up.
          // Rebaseline for the same reason as above.
          self.previousCart = newCart;
          return;
        }

        // `cleanupInFlight` is already true (set unconditionally at
        // the start of `runCascade`), so the section-refresh below
        // can dispatch its own `update:cart-line-item` event without
        // re-entering this diff.
        return self
          .removeLines(orphans)
          .then(function (updatedCart) {
            // /cart/update.js returns the authoritative cart — use its
            // item_count to fix the header bubbles, which the section
            // refresh below can't reach.
            updateCartCountBubbles(updatedCart);
            return refreshCartSections();
          })
          .then(function () {
            return step();
          });
      });
    }

    this.cleanupInFlight = true;
    return step()
      .catch(function (e) {
        console.warn(
          "[bundle-perks-cascade] cascade failed, leaving orphaned perks for the customer to remove manually",
          e
        );
      })
      .then(function () {
        // Defer the flag reset to the next tick so the
        // `update:cart-line-item` event our refresh dispatched
        // doesn't re-enter the diff path before the queue drains.
        setTimeout(function () {
          self.cleanupInFlight = false;
        }, 0);
      });
  };

  CartCascade.prototype.removeLines = function (items) {
    var updates = {};
    items.forEach(function (item) {
      if (item && item.key) updates[item.key] = 0;
    });

    return fetch(UPDATE_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ updates: updates }),
    }).then(function (res) {
      if (!res.ok) throw new Error("HTTP " + res.status + " " + UPDATE_URL);
      return res.json();
    });
  };

  // ---------------------------------------------------------------------------
  // Cart line sort
  //
  // Re-orders visible cart item rows so:
  //   1 — bundle parent line (has _bundle_included_perks)
  //   2 — threshold-unlocked perk lines (_added_from === "threshold_perks")
  //   3 — bundle add-on lines (_added_from_location === "add-ons")
  //   4 — everything else
  //
  // Sort is stable within each group (Array.sort is stable in all modern
  // engines). The priority is read directly from `data-cart-sort-priority`
  // attributes stamped onto the items by Liquid at render time, so no
  // extra network request is needed and stale-snapshot issues don't apply.
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Grouped sort helpers
  //
  // Goal: [BundleA, PerkA1, PerkA2, AddonA1, BundleB, PerkB1, AddonB1, Other]
  //
  // Each DOM node carries two Liquid-stamped attributes:
  //   data-cart-sort-priority  — 1=bundle, 2=perk, 3=addon, 4=other
  //   data-bundle-group-id     — the bg_… token shared by a bundle and all
  //                              its perks + add-ons (empty for "other" items)
  //
  // Algorithm (pure DOM, no network fetch):
  //   1. Walk children in their current DOM order.
  //   2. When we first see a bundle group id, assign it the next "group order"
  //      integer. Bundles are always priority=1 so they're always the first
  //      element seen for their group.
  //   3. Each item's sort key = groupOrder * 10 + typeWithinGroup.
  //      Items with no group id get groupOrder = (number of groups + 1),
  //      so they naturally land at the end in their original relative order.
  // ---------------------------------------------------------------------------

  function reorderChildren(container) {
    if (!container) return;
    var children = Array.from(container.children);
    if (children.length < 2) return;

    // Pass 1: assign group order by first-seen bundle group id.
    var groupOrderMap = {};
    var nextGroupOrder = 1;
    children.forEach(function (el) {
      var gid = el.getAttribute("data-bundle-group-id") || "";
      if (gid && groupOrderMap[gid] === undefined) {
        groupOrderMap[gid] = nextGroupOrder++;
      }
    });
    var ungroupedOrder = nextGroupOrder; // everything without a group id sorts last

    // Pass 2: compute composite sort key for each child.
    var scored = children.map(function (el, originalIndex) {
      var gid = el.getAttribute("data-bundle-group-id") || "";
      var typePriority = parseInt(
        el.getAttribute("data-cart-sort-priority") || "99",
        10
      );
      var groupOrder =
        gid && groupOrderMap[gid] !== undefined
          ? groupOrderMap[gid]
          : ungroupedOrder;
      return {
        el: el,
        originalIndex: originalIndex,
        key: groupOrder * 10 + typePriority,
      };
    });

    // Stable sort: Array.sort is stable in all modern engines (V8 ≥ 7,
    // all Safari/Firefox). Equal keys keep originalIndex order.
    scored.sort(function (a, b) {
      return a.key - b.key;
    });

    // Bail out early if nothing needs to move.
    var needsMove = scored.some(function (item, i) {
      return item.el !== children[i];
    });
    if (!needsMove) return;

    scored.forEach(function (item) {
      container.appendChild(item.el); // move, not copy
    });
  }

  function sortCartLineItems() {
    // Cart drawer: children of `.flex.flex-col.gap-7` are the outer wrappers.
    var drawerGrid = document.querySelector("#CartDrawer-CartItems .flex");
    reorderChildren(drawerGrid);

    // Cart page: `CartItem-N` divs are direct children of `.js-contents .grid`.
    var pageGrid = document.querySelector(".js-contents .grid");
    reorderChildren(pageGrid);
  }

  function init() {
    var cascade = new CartCascade();
    cascade.start();
    window.__bundlePerksCascade = cascade;

    // Sort on every cart update. The event is emitted by the theme's own
    // CartItems/CartDrawerItems after section HTML is re-injected, AND by
    // our own refreshCartSections() after a cascade cleanup. By the time
    // this handler runs the DOM already has the new HTML with fresh
    // `data-cart-sort-priority` attributes stamped by Liquid.
    document.addEventListener("update:cart-line-item", function () {
      sortCartLineItems();
    });

    // Sort the initial server-rendered Liquid output (page load, no JS
    // cart update has fired yet).
    sortCartLineItems();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
